import { IConsultationSettings, LMSLessonClassTypeModel } from "./LessonsEditorTypes";

// Функция для получения названия группы на текущем языке
export const getGroupName = (
  group: { groupId: string; name: Array<{ lang: string; name: string }> },
  courseGroups: Array<{ groupId: string; name: Array<{ lang: string; name: string }> }>,
  currentLanguage: string,
  t?: (key: string, params?: any) => string
): string => {
  const langName = currentLanguage.toLowerCase();
  const nameItem = group.name.find((n) => n.lang.toLowerCase() === langName);
  if (nameItem && nameItem.name) {
    return nameItem.name;
  }
  const firstNonEmpty = group.name.find((n) => n.name);
  if (firstNonEmpty) {
    return firstNonEmpty.name;
  }
  const groupIndex = courseGroups.findIndex((g) => g.groupId === group.groupId);
  if (t) {
    return t('lms.lessonsEditor.defaultGroup', { index: groupIndex + 1 });
  }
  return `Группа ${groupIndex + 1}`;
};

// Функция для проверки, является ли строка URL
export const isValidUrl = (string: string): boolean => {
  if (!string) return false;
  const trimmed = string.trim();
  if (!trimmed) return false;

  if (/\s/.test(trimmed)) return false;

  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  }

  if (/^[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}(\/[^\s]*)?$/.test(trimmed)) {
    try {
      const url = new URL(`https://${trimmed}`);
      return url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  }

  return false;
};

// Функция для получения названия дня недели
export const getDayOfWeekLabel = (date: Date | null, t?: (key: string) => string): string => {
  if (!date) return '';
  const dayOfWeek = date.getDay();
  if (t) {
    const dayMap: Record<number, string> = {
      0: t('lms.lessonsEditor.weekDays.sun'),
      1: t('lms.lessonsEditor.weekDays.mon'),
      2: t('lms.lessonsEditor.weekDays.tue'),
      3: t('lms.lessonsEditor.weekDays.wed'),
      4: t('lms.lessonsEditor.weekDays.thu'),
      5: t('lms.lessonsEditor.weekDays.fri'),
      6: t('lms.lessonsEditor.weekDays.sat'),
    };
    return dayMap[dayOfWeek] || '';
  }
  const dayMap: Record<number, string> = {
    0: 'ВС',
    1: 'ПН',
    2: 'ВТ',
    3: 'СР',
    4: 'ЧТ',
    5: 'ПТ',
    6: 'СБ',
  };
  return dayMap[dayOfWeek] || '';
};

// Функция для форматирования даты
export const formatDate = (date: Date | null): string => {
  if (!date) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}`;
};

// Функция для извлечения времени из ISO строки
export const extractTimeFromISO = (isoString: string): string => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch (e) {
    return '';
  }
};

export const cloneDate = (date: Date | null | undefined): Date | null => (
  date ? new Date(date.getTime()) : null
);

export const dateFromISO = (isoString?: string | null): Date | null => {
  if (!isoString) return null;
  const date = new Date(isoString);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Функция для объединения даты и времени
export const combineDateAndTime = (date: Date | null, time: string): string | null => {
  if (!date || !time) return null;
  
  const [hours, minutes] = time.split(':');
  const combinedDate = new Date(date);
  combinedDate.setHours(parseInt(hours, 10));
  combinedDate.setMinutes(parseInt(minutes, 10));
  combinedDate.setSeconds(0);
  combinedDate.setMilliseconds(0);
  
  return combinedDate.toISOString();
};

// Функция для генерации всех дат для выбранных дней недели в указанном периоде
export const generateDatesForWeekdays = (startDate: Date, endDate: Date, weekdays: number[]): Date[] => {
  const dates: Date[] = [];
  const currentDate = new Date(startDate);
  
  currentDate.setHours(0, 0, 0, 0);
  const normalizedEndDate = new Date(endDate);
  normalizedEndDate.setHours(23, 59, 59, 999);
  
  while (currentDate <= normalizedEndDate) {
    const dayOfWeek = currentDate.getDay(); 
    
    if (weekdays.includes(dayOfWeek)) {
      dates.push(new Date(currentDate));
    }
    
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return dates;
};

export const areGroupIdsEqual = (ids1: string[], ids2: string[]): boolean => {
  if (ids1.length !== ids2.length) return false;
  const sorted1 = [...ids1].sort();
  const sorted2 = [...ids2].sort();
  return sorted1.every((id, index) => id === sorted2[index]);
};

// Приводит значение classType (число/строка из API) к enum LMSLessonClassTypeModel.
export const normalizeLessonClassType = (value: unknown): LMSLessonClassTypeModel => {
  if (typeof value === 'number' && LMSLessonClassTypeModel[value] !== undefined) {
    return value as LMSLessonClassTypeModel;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return LMSLessonClassTypeModel.Ordinal;
    const num = Number(trimmed);
    if (!Number.isNaN(num) && LMSLessonClassTypeModel[num] !== undefined) {
      return num as LMSLessonClassTypeModel;
    }
    if (trimmed in LMSLessonClassTypeModel) {
      return LMSLessonClassTypeModel[trimmed as keyof typeof LMSLessonClassTypeModel] as LMSLessonClassTypeModel;
    }
  }
  return LMSLessonClassTypeModel.Ordinal;
};

type DuplicateCheckSlot = {
  start: string;
  end?: string;
  groupIds: string[];
  classType?: unknown;
  teacherId?: string;
  consultation?: { teacherId?: string };
};

const getSlotTeacherId = (slot: DuplicateCheckSlot): string =>
  String(slot?.consultation?.teacherId || slot?.teacherId || '');

const isSameDateTime = (a?: string, b?: string): boolean => {
  const parsedA = a ? Date.parse(a) : NaN;
  const parsedB = b ? Date.parse(b) : NaN;
  if (Number.isNaN(parsedA) || Number.isNaN(parsedB)) return (a || '') === (b || '');
  return parsedA === parsedB;
};

export const isDuplicateScheduleSlot = (
  a: DuplicateCheckSlot,
  b: DuplicateCheckSlot,
): boolean => {
  return isSameDateTime(a.start, b.start)
    && isSameDateTime(a.end, b.end)
    && areGroupIdsEqual(a.groupIds || [], b.groupIds || [])
    && normalizeLessonClassType(a.classType) === normalizeLessonClassType(b.classType)
    && getSlotTeacherId(a) === getSlotTeacherId(b);
};

export const getConsultationSettingsFromApi = (lesson: any): IConsultationSettings | undefined => {
  if (normalizeLessonClassType(lesson?.classType) !== LMSLessonClassTypeModel.Consultation) {
    return undefined;
  }

  const format = lesson?.consultationFormat;
  const isOffline = format === 1 || String(format).toLowerCase() === 'offline';
  const openingAppointmentIn = Number(lesson?.openingAppointmentIn);

  const settings: IConsultationSettings = {
    capacity: lesson?.studentsMaxLimit ?? undefined,
    timePerPerson: lesson?.timeSlotPerRerson ?? undefined,
    format: isOffline ? 'offline' : 'online',
    registrationDaysBefore: Number.isFinite(openingAppointmentIn)
      ? Math.round(openingAppointmentIn / 24)
      : undefined,
    teacherId: lesson?.teacherId ?? undefined,
    isInvitedTeacher: Boolean(lesson?.isGuestTeacher),
  };

  return settings;
};

export const getConsultationPayload = (consultation?: {
  capacity?: number;
  timePerPerson?: number;
  format?: 'online' | 'offline';
  registrationDaysBefore?: number;
  teacherId?: string;
  isInvitedTeacher?: boolean;
}) => {
  if (!consultation) return {};

  return {
    studentsMaxLimit: consultation.capacity,
    timeSlotPerRerson: consultation.timePerPerson,
    openingAppointmentIn: consultation.registrationDaysBefore == null
      ? undefined
      : consultation.registrationDaysBefore * 24,
    teacherId: consultation.teacherId,
    isGuestTeacher: consultation.isInvitedTeacher,
    consultationFormat: consultation.format === 'offline' ? 1 : 0,
  };
};

/** Parses "HH:mm" / "HH:mm:ss" to minutes from midnight. */
export const parseTimeToMinutes = (time?: string | null): number | null => {
  if (!time) return null;
  const match = String(time).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};

export const getConsultationDurationMinutes = (
  start?: string | null,
  end?: string | null,
): number | null => {
  const startMin = parseTimeToMinutes(start);
  const endMin = parseTimeToMinutes(end);
  if (startMin == null || endMin == null || endMin <= startMin) return null;
  return endMin - startMin;
};

export const calcMaxTimePerPerson = (
  durationMinutes: number,
  capacity: number,
): number | null => {
  if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) return null;
  if (!Number.isFinite(capacity) || capacity <= 0) return null;
  const value = Math.floor(durationMinutes / capacity);
  return value > 0 ? value : null;
};

export const calcMaxCapacity = (
  durationMinutes: number,
  timePerPerson: number,
): number | null => {
  if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) return null;
  if (!Number.isFinite(timePerPerson) || timePerPerson <= 0) return null;
  const value = Math.floor(durationMinutes / timePerPerson);
  return value > 0 ? value : null;
};

export type ConsultationCapacityCheckResult =
  | { ok: true; maxSlots: number; durationMinutes: number }
  | {
      ok: false;
      reason: 'missing_time' | 'invalid_time_range' | 'missing_capacity' | 'overflow';
      maxSlots?: number;
      durationMinutes?: number;
    };

/**
 * Ensures capacity × timePerPerson fits into lesson duration (start–end).
 * maxSlots = floor(durationMinutes / timePerPerson).
 */
export const checkConsultationCapacityFit = (params: {
  start?: string | null;
  end?: string | null;
  capacity?: number | string | null;
  timePerPerson?: number | null;
}): ConsultationCapacityCheckResult => {
  const { start, end, timePerPerson } = params;
  if (!start || !end) {
    return { ok: false, reason: 'missing_time' };
  }

  const startMin = parseTimeToMinutes(start);
  const endMin = parseTimeToMinutes(end);
  if (startMin == null || endMin == null) {
    return { ok: false, reason: 'missing_time' };
  }
  if (endMin <= startMin) {
    return { ok: false, reason: 'invalid_time_range' };
  }

  const durationMinutes = endMin - startMin;
  const capacity = Number(params.capacity);
  if (!Number.isFinite(capacity) || capacity <= 0 || !timePerPerson || timePerPerson <= 0) {
    return { ok: false, reason: 'missing_capacity', durationMinutes };
  }

  const maxSlots = Math.floor(durationMinutes / timePerPerson);
  if (capacity > maxSlots) {
    return { ok: false, reason: 'overflow', maxSlots, durationMinutes };
  }

  return { ok: true, maxSlots, durationMinutes };
};

// Форматирует диапазон времени из ISO start/end, возвращает прочерк если данных нет.
export const formatTimeRange = (start?: string, end?: string, emptyValue = '—'): string => {
  if (!start || !end) return emptyValue;
  return `${extractTimeFromISO(start)} - ${extractTimeFromISO(end)}`;
};

// Обрезает строку до maxLength символов, добавляя '...' если строка длиннее.
export const truncateLabel = (label: string, maxLength: number): string =>
  label.length > maxLength ? `${label.slice(0, maxLength)}...` : label;

// Префикс id для уроков, ещё не сохранённых на сервере.
const NEW_LESSON_ID_PREFIX = 'new_lesson';

export const isNewLessonId = (id: string): boolean => id.startsWith(NEW_LESSON_ID_PREFIX);

// Генерирует уникальный id для нового локального урока.
export const generateNewLessonId = (): string => {
  const random = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${NEW_LESSON_ID_PREFIX}-${random}`;
};import { IConsultationSettings, LMSLessonClassTypeModel } from "./LessonsEditorTypes";

// Функция для получения названия группы на текущем языке
export const getGroupName = (
  group: { groupId: string; name: Array<{ lang: string; name: string }> },
  courseGroups: Array<{ groupId: string; name: Array<{ lang: string; name: string }> }>,
  currentLanguage: string,
  t?: (key: string, params?: any) => string
): string => {
  const langName = currentLanguage.toLowerCase();
  const nameItem = group.name.find((n) => n.lang.toLowerCase() === langName);
  if (nameItem && nameItem.name) {
    return nameItem.name;
  }
  const firstNonEmpty = group.name.find((n) => n.name);
  if (firstNonEmpty) {
    return firstNonEmpty.name;
  }
  const groupIndex = courseGroups.findIndex((g) => g.groupId === group.groupId);
  if (t) {
    return t('lms.lessonsEditor.defaultGroup', { index: groupIndex + 1 });
  }
  return `Группа ${groupIndex + 1}`;
};

// Функция для проверки, является ли строка URL
export const isValidUrl = (string: string): boolean => {
  if (!string) return false;
  const trimmed = string.trim();
  if (!trimmed) return false;

  if (/\s/.test(trimmed)) return false;

  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  }

  if (/^[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}(\/[^\s]*)?$/.test(trimmed)) {
    try {
      const url = new URL(`https://${trimmed}`);
      return url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  }

  return false;
};

// Функция для получения названия дня недели
export const getDayOfWeekLabel = (date: Date | null, t?: (key: string) => string): string => {
  if (!date) return '';
  const dayOfWeek = date.getDay();
  if (t) {
    const dayMap: Record<number, string> = {
      0: t('lms.lessonsEditor.weekDays.sun'),
      1: t('lms.lessonsEditor.weekDays.mon'),
      2: t('lms.lessonsEditor.weekDays.tue'),
      3: t('lms.lessonsEditor.weekDays.wed'),
      4: t('lms.lessonsEditor.weekDays.thu'),
      5: t('lms.lessonsEditor.weekDays.fri'),
      6: t('lms.lessonsEditor.weekDays.sat'),
    };
    return dayMap[dayOfWeek] || '';
  }
  const dayMap: Record<number, string> = {
    0: 'ВС',
    1: 'ПН',
    2: 'ВТ',
    3: 'СР',
    4: 'ЧТ',
    5: 'ПТ',
    6: 'СБ',
  };
  return dayMap[dayOfWeek] || '';
};

// Функция для форматирования даты
export const formatDate = (date: Date | null): string => {
  if (!date) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}`;
};

// Функция для извлечения времени из ISO строки
export const extractTimeFromISO = (isoString: string): string => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch (e) {
    return '';
  }
};

export const cloneDate = (date: Date | null | undefined): Date | null => (
  date ? new Date(date.getTime()) : null
);

export const dateFromISO = (isoString?: string | null): Date | null => {
  if (!isoString) return null;
  const date = new Date(isoString);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Функция для объединения даты и времени
export const combineDateAndTime = (date: Date | null, time: string): string | null => {
  if (!date || !time) return null;
  
  const [hours, minutes] = time.split(':');
  const combinedDate = new Date(date);
  combinedDate.setHours(parseInt(hours, 10));
  combinedDate.setMinutes(parseInt(minutes, 10));
  combinedDate.setSeconds(0);
  combinedDate.setMilliseconds(0);
  
  return combinedDate.toISOString();
};

// Функция для генерации всех дат для выбранных дней недели в указанном периоде
export const generateDatesForWeekdays = (startDate: Date, endDate: Date, weekdays: number[]): Date[] => {
  const dates: Date[] = [];
  const currentDate = new Date(startDate);
  
  currentDate.setHours(0, 0, 0, 0);
  const normalizedEndDate = new Date(endDate);
  normalizedEndDate.setHours(23, 59, 59, 999);
  
  while (currentDate <= normalizedEndDate) {
    const dayOfWeek = currentDate.getDay(); 
    
    if (weekdays.includes(dayOfWeek)) {
      dates.push(new Date(currentDate));
    }
    
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return dates;
};

export const areGroupIdsEqual = (ids1: string[], ids2: string[]): boolean => {
  if (ids1.length !== ids2.length) return false;
  const sorted1 = [...ids1].sort();
  const sorted2 = [...ids2].sort();
  return sorted1.every((id, index) => id === sorted2[index]);
};

// Приводит значение classType (число/строка из API) к enum LMSLessonClassTypeModel.
export const normalizeLessonClassType = (value: unknown): LMSLessonClassTypeModel => {
  if (typeof value === 'number' && LMSLessonClassTypeModel[value] !== undefined) {
    return value as LMSLessonClassTypeModel;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return LMSLessonClassTypeModel.Ordinal;
    const num = Number(trimmed);
    if (!Number.isNaN(num) && LMSLessonClassTypeModel[num] !== undefined) {
      return num as LMSLessonClassTypeModel;
    }
    if (trimmed in LMSLessonClassTypeModel) {
      return LMSLessonClassTypeModel[trimmed as keyof typeof LMSLessonClassTypeModel] as LMSLessonClassTypeModel;
    }
  }
  return LMSLessonClassTypeModel.Ordinal;
};

type DuplicateCheckSlot = {
  start: string;
  end?: string;
  groupIds: string[];
  classType?: unknown;
  teacherId?: string;
  consultation?: { teacherId?: string };
};

const getSlotTeacherId = (slot: DuplicateCheckSlot): string =>
  String(slot?.consultation?.teacherId || slot?.teacherId || '');

const isSameDateTime = (a?: string, b?: string): boolean => {
  const parsedA = a ? Date.parse(a) : NaN;
  const parsedB = b ? Date.parse(b) : NaN;
  if (Number.isNaN(parsedA) || Number.isNaN(parsedB)) return (a || '') === (b || '');
  return parsedA === parsedB;
};

export const isDuplicateScheduleSlot = (
  a: DuplicateCheckSlot,
  b: DuplicateCheckSlot,
): boolean => {
  return isSameDateTime(a.start, b.start)
    && isSameDateTime(a.end, b.end)
    && areGroupIdsEqual(a.groupIds || [], b.groupIds || [])
    && normalizeLessonClassType(a.classType) === normalizeLessonClassType(b.classType)
    && getSlotTeacherId(a) === getSlotTeacherId(b);
};

export const getConsultationSettingsFromApi = (lesson: any): IConsultationSettings | undefined => {
  if (normalizeLessonClassType(lesson?.classType) !== LMSLessonClassTypeModel.Consultation) {
    return undefined;
  }

  const format = lesson?.consultationFormat;
  const isOffline = format === 1 || String(format).toLowerCase() === 'offline';
  const openingAppointmentIn = Number(lesson?.openingAppointmentIn);

  const settings: IConsultationSettings = {
    capacity: lesson?.studentsMaxLimit ?? undefined,
    timePerPerson: lesson?.timeSlotPerRerson ?? undefined,
    format: isOffline ? 'offline' : 'online',
    registrationDaysBefore: Number.isFinite(openingAppointmentIn)
      ? Math.round(openingAppointmentIn / 24)
      : undefined,
    teacherId: lesson?.teacherId ?? undefined,
    isInvitedTeacher: Boolean(lesson?.isGuestTeacher),
  };

  return settings;
};

export const getConsultationPayload = (consultation?: {
  capacity?: number;
  timePerPerson?: number;
  format?: 'online' | 'offline';
  registrationDaysBefore?: number;
  teacherId?: string;
  isInvitedTeacher?: boolean;
}) => {
  if (!consultation) return {};

  return {
    studentsMaxLimit: consultation.capacity,
    timeSlotPerRerson: consultation.timePerPerson,
    openingAppointmentIn: consultation.registrationDaysBefore == null
      ? undefined
      : consultation.registrationDaysBefore * 24,
    teacherId: consultation.teacherId,
    isGuestTeacher: consultation.isInvitedTeacher,
    consultationFormat: consultation.format === 'offline' ? 1 : 0,
  };
};

/** Parses "HH:mm" / "HH:mm:ss" to minutes from midnight. */
export const parseTimeToMinutes = (time?: string | null): number | null => {
  if (!time) return null;
  const match = String(time).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};

export const getConsultationDurationMinutes = (
  start?: string | null,
  end?: string | null,
): number | null => {
  const startMin = parseTimeToMinutes(start);
  const endMin = parseTimeToMinutes(end);
  if (startMin == null || endMin == null || endMin <= startMin) return null;
  return endMin - startMin;
};

export const calcMaxTimePerPerson = (
  durationMinutes: number,
  capacity: number,
): number | null => {
  if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) return null;
  if (!Number.isFinite(capacity) || capacity <= 0) return null;
  const value = Math.floor(durationMinutes / capacity);
  return value > 0 ? value : null;
};

export const calcMaxCapacity = (
  durationMinutes: number,
  timePerPerson: number,
): number | null => {
  if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) return null;
  if (!Number.isFinite(timePerPerson) || timePerPerson <= 0) return null;
  const value = Math.floor(durationMinutes / timePerPerson);
  return value > 0 ? value : null;
};

export type ConsultationCapacityCheckResult =
  | { ok: true; maxSlots: number; durationMinutes: number }
  | {
      ok: false;
      reason: 'missing_time' | 'invalid_time_range' | 'missing_capacity' | 'overflow';
      maxSlots?: number;
      durationMinutes?: number;
    };

/**
 * Ensures capacity × timePerPerson fits into lesson duration (start–end).
 * maxSlots = floor(durationMinutes / timePerPerson).
 */
export const checkConsultationCapacityFit = (params: {
  start?: string | null;
  end?: string | null;
  capacity?: number | string | null;
  timePerPerson?: number | null;
}): ConsultationCapacityCheckResult => {
  const { start, end, timePerPerson } = params;
  if (!start || !end) {
    return { ok: false, reason: 'missing_time' };
  }

  const startMin = parseTimeToMinutes(start);
  const endMin = parseTimeToMinutes(end);
  if (startMin == null || endMin == null) {
    return { ok: false, reason: 'missing_time' };
  }
  if (endMin <= startMin) {
    return { ok: false, reason: 'invalid_time_range' };
  }

  const durationMinutes = endMin - startMin;
  const capacity = Number(params.capacity);
  if (!Number.isFinite(capacity) || capacity <= 0 || !timePerPerson || timePerPerson <= 0) {
    return { ok: false, reason: 'missing_capacity', durationMinutes };
  }

  const maxSlots = Math.floor(durationMinutes / timePerPerson);
  if (capacity > maxSlots) {
    return { ok: false, reason: 'overflow', maxSlots, durationMinutes };
  }

  return { ok: true, maxSlots, durationMinutes };
};

// Форматирует диапазон времени из ISO start/end, возвращает прочерк если данных нет.
export const formatTimeRange = (start?: string, end?: string, emptyValue = '—'): string => {
  if (!start || !end) return emptyValue;
  return `${extractTimeFromISO(start)} - ${extractTimeFromISO(end)}`;
};

// Обрезает строку до maxLength символов, добавляя '...' если строка длиннее.
export const truncateLabel = (label: string, maxLength: number): string =>
  label.length > maxLength ? `${label.slice(0, maxLength)}...` : label;

// Префикс id для уроков, ещё не сохранённых на сервере.
const NEW_LESSON_ID_PREFIX = 'new_lesson';

export const isNewLessonId = (id: string): boolean => id.startsWith(NEW_LESSON_ID_PREFIX);

// Генерирует уникальный id для нового локального урока.
export const generateNewLessonId = (): string => {
  const random = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${NEW_LESSON_ID_PREFIX}-${random}`;
};